import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config/env";

/**
 * AuthContext — single source of truth for the authentication lifecycle.
 *
 * Explicit lifecycle methods:
 *   bootstrap()     — restore session from HttpOnly cookies on app load
 *   refreshUser()   — re-fetch /auth/me (e.g. after email verification)
 *   clearSession()  — wipe local auth state (used by logout / hard failures)
 *   login / register / logout / resendVerification / verifyEmail
 *
 * Every async auth call uses a timeout and AbortController so loading state
 * can never hang indefinitely.
 */

const AUTH_TIMEOUT_MS = 15000;

axios.defaults.withCredentials = true;
axios.defaults.timeout = AUTH_TIMEOUT_MS;

const AuthContext = createContext(null);

function isAuthenticatedUser(data) {
  return Boolean(data && data.id);
}

export const AuthProvider = ({ children }) => {
  // null = unknown (bootstrapping), false = anonymous, object = signed-in user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const bootstrapAbortRef = useRef(null);

  const clearSession = useCallback(() => {
    setUser(false);
    setAuthError(null);
  }, []);

  const refreshUser = useCallback(async ({ signal } = {}) => {
    const { data } = await axios.get(`${API}/auth/me`, {
      validateStatus: (s) => s < 500,
      signal,
      timeout: AUTH_TIMEOUT_MS,
    });
    if (isAuthenticatedUser(data)) {
      setUser(data);
      setAuthError(null);
      return data;
    }
    setUser(false);
    return false;
  }, []);

  const bootstrap = useCallback(async () => {
    if (bootstrapAbortRef.current) {
      bootstrapAbortRef.current.abort();
    }
    const controller = new AbortController();
    bootstrapAbortRef.current = controller;
    setLoading(true);
    setAuthError(null);
    try {
      await refreshUser({ signal: controller.signal });
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        return;
      }
      setUser(false);
      setAuthError(err?.message || "Could not restore session.");
    } finally {
      if (bootstrapAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [refreshUser]);

  useEffect(() => {
    bootstrap();
    return () => {
      if (bootstrapAbortRef.current) bootstrapAbortRef.current.abort();
    };
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    setAuthError(null);
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { timeout: AUTH_TIMEOUT_MS },
    );
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    setAuthError(null);
    const { data } = await axios.post(`${API}/auth/register`, payload, {
      timeout: AUTH_TIMEOUT_MS,
    });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { timeout: AUTH_TIMEOUT_MS });
    } catch {
      /* server clear failed — still wipe local session */
    }
    clearSession();
  }, [clearSession]);

  const resendVerification = useCallback(async () => {
    const { data } = await axios.post(`${API}/auth/resend-verification`, null, {
      timeout: AUTH_TIMEOUT_MS,
    });
    return data;
  }, []);

  /**
   * Verify email token via AuthContext so the session user is refreshed
   * immediately after success (email_verified flips without a full reload).
   */
  const verifyEmail = useCallback(async (token, { signal } = {}) => {
    const { data } = await axios.post(
      `${API}/auth/verify-email`,
      { token },
      { timeout: AUTH_TIMEOUT_MS, signal },
    );
    try {
      await refreshUser({ signal });
    } catch {
      /* verification succeeded even if session refresh fails */
    }
    return data;
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authError,
        isAuthenticated: isAuthenticatedUser(user),
        bootstrap,
        refreshUser,
        clearSession,
        login,
        register,
        logout,
        resendVerification,
        verifyEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};

export { API, AUTH_TIMEOUT_MS };
